import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import type { UserPlan, Workbench } from '@minglun/domain';
import { clearDeviceSession, deviceFetch, type AuthorizedDevice } from './device-security';
import { createSessionFetch, isInvalidSessionError, readSessionSafely, SESSION_EXPIRED_MESSAGE } from './auth-session';

export type AccountRole = 'user' | 'admin';
export type AccountStatus = 'active' | 'disabled';
export interface AccountProfile {
  id: string;
  email: string;
  displayName: string;
  role: AccountRole;
  status: AccountStatus;
  expiresAt?: string;
  features: string[];
  mustSetPassword: boolean;
  deviceLimit: number;
  weeklyReplacementLimit: number;
  studyLimit: number;
  resultLimit: number;
  devices: AuthorizedDevice[];
  usage: { studies: number; results: number };
  isSuperAdmin: boolean;
  canManage: boolean;
}

export interface LoginPreferences {
  email: string;
  rememberCredentials: boolean;
  autoLogin: boolean;
}

const REMEMBERED_EMAIL_KEY = 'minglun.auth.remembered-email';
const AUTO_LOGIN_KEY = 'minglun.auth.auto-login';

const authStorage = {
  getItem(key: string): string | null {
    return autoLoginEnabled()
      ? safeGet(localStorage, key) ?? safeGet(sessionStorage, key)
      : safeGet(sessionStorage, key);
  },
  setItem(key: string, value: string): void {
    if (autoLoginEnabled()) {
      safeSet(localStorage, key, value);
      safeRemove(sessionStorage, key);
    } else {
      safeSet(sessionStorage, key, value);
      safeRemove(localStorage, key);
    }
  },
  removeItem(key: string): void {
    safeRemove(localStorage, key);
    safeRemove(sessionStorage, key);
  },
};

const url = import.meta.env.VITE_SUPABASE_URL?.trim();
const anonymousKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
export const initialAuthCallbackIssue = readAuthCallbackIssue();
export const demoMode = import.meta.env.VITE_DEMO_MODE === 'true' || (import.meta.env.DEV && (!url || !anonymousKey));
export const cloudConfigured = Boolean(url && anonymousKey);
let authNotice: string | undefined;
let intentionalSignOuts = 0;
export const supabase: SupabaseClient | undefined = cloudConfigured
  ? createClient(url!, anonymousKey!, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, storage: authStorage },
      global: { fetch: createSessionFetch() },
    })
  : undefined;

// Register before AppShell mounts: initialization itself can discover a stale
// refresh token. Only transient device-session credentials are cleared here;
// the IndexedDB device identity and all workspace caches remain untouched.
supabase?.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    authNotice = intentionalSignOuts ? undefined : SESSION_EXPIRED_MESSAGE;
    clearDeviceSession();
  } else if ((event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') && session) {
    authNotice = undefined;
  }
});

export function currentAuthNotice(): string | undefined { return authNotice; }

export function readLoginPreferences(): LoginPreferences {
  const email = safeGet(localStorage, REMEMBERED_EMAIL_KEY) ?? '';
  const autoLogin = autoLoginEnabled();
  return { email, rememberCredentials: Boolean(email) || autoLogin, autoLogin };
}

export function configureLoginPreferences(email: string, rememberCredentials: boolean, autoLogin: boolean): void {
  safeSet(localStorage, AUTO_LOGIN_KEY, autoLogin ? 'true' : 'false');
  if (rememberCredentials || autoLogin) safeSet(localStorage, REMEMBERED_EMAIL_KEY, email.trim().toLowerCase());
  else safeRemove(localStorage, REMEMBERED_EMAIL_KEY);
}

export async function currentSession(): Promise<Session | undefined> {
  if (demoMode) return undefined;
  if (!supabase) throw new Error('站点尚未配置托管账户服务。');
  return readSessionSafely(supabase.auth);
}

export async function loadProfile(session: Session | undefined): Promise<AccountProfile> {
  if (demoMode) {
    return { id: 'demo-user', email: 'demo@local', displayName: '本地预览', role: 'admin', status: 'active', features: ['desktop', 'solver', 'developer'], mustSetPassword: false, deviceLimit: 2, weeklyReplacementLimit: 2, studyLimit: 5, resultLimit: 5, devices: [], usage: { studies: 0, results: 0 }, isSuperAdmin: true, canManage: true };
  }
  if (!supabase || !session) throw new Error('登录状态已失效，请重新登录。');
  const { data, error } = await supabase.from('profiles').select('*').eq('id', session.user.id).single();
  if (error) throw readableError(error, '无法读取账户资料。');
  return profileFromRow(data as Record<string, unknown>);
}

const workspaceRevisions=new Map<string,string|null>();
const revisionGuardAccounts=new Set<string>();
export async function loadWorkspace(profile: AccountProfile, session: Session): Promise<{ workbench?: Workbench; userPlan?: UserPlan }> {
  const cached = readCachedWorkspace(profile.id);
  if(cached && 'updatedAt' in cached)workspaceRevisions.set(profile.id,cached.updatedAt??null);
  if (demoMode || !supabase) return cached ?? {};
  try {
    type WorkspaceRow={workbench:Workbench;user_plan:UserPlan;updated_at:string};
    const response = await deviceFetch<{revisionGuard:true;workspace:WorkspaceRow|null} | WorkspaceRow | null>(session, '/api/workspace?revisionGuard=1');
    const guarded=Boolean(response && 'revisionGuard' in response && response.revisionGuard===true);
    if(guarded)revisionGuardAccounts.add(profile.id);else revisionGuardAccounts.delete(profile.id);
    const data=response && 'revisionGuard' in response?response.workspace:response;
    // A successful empty response is authoritative: this account has no
    // workspace yet. Never resurrect a browser cache in that case.
    if (!data) { workspaceRevisions.set(profile.id,null);clearCachedWorkspace(profile.id); return {}; }
    workspaceRevisions.set(profile.id,data.updated_at);
    const workspace = { workbench: data.workbench, userPlan: data.user_plan };
    cacheWorkspace(profile.id, {...workspace,updatedAt:data.updated_at});
    return workspace;
  } catch (error) {
    if (cached) return cached;
    throw error;
  }
}

export async function loadPublishedCatalog(session: Session): Promise<Workbench | undefined> {
  if (demoMode || !supabase) return undefined;
  const data = await deviceFetch<{ workbench?: Workbench } | null>(session, '/api/catalog');
  return data?.workbench;
}

export async function saveWorkspace(profile: AccountProfile, session: Session, workbench: Workbench, userPlan: UserPlan): Promise<void> {
  const workspace = { workbench: structuredClone(workbench), userPlan: structuredClone(userPlan) };
  if (demoMode || !supabase) { cacheWorkspace(profile.id,workspace);return; }
  if(!revisionGuardAccounts.has(profile.id))throw new Error('暂时无法确认云端版本保护。本机草稿保留，请联网后重新打开工作台再同步。');
  if(!workspaceRevisions.has(profile.id))throw new Error('无法确认云端版本，本机草稿已保留。请联网后重新打开工作台。');
  const saved=await deviceFetch<{ok:boolean;updated_at?:string}>(session, '/api/workspace', { method: 'PUT', body: JSON.stringify({ workbench, userPlan, expectedUpdatedAt:workspaceRevisions.get(profile.id) }) });
  if(!saved.updated_at)throw new Error('云端尚未支持版本校验，本机草稿已保留，请勿关闭此页。');
  workspaceRevisions.set(profile.id,saved.updated_at);
  cacheWorkspace(profile.id, {...workspace,updatedAt:saved.updated_at});
}

export async function signIn(email: string, password: string): Promise<void> {
  if (!supabase) throw new Error('站点尚未配置托管账户服务。');
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
  if (error) throw authError(error, '登录失败，请稍后重试。');
}

export async function requestInitialPasswordLink(email: string): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (!/.+@.+\..+/.test(normalized)) throw new Error('请先输入受邀邮箱。');
  const response = await fetch('/api/account/registration', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email: normalized }),
  });
  if (response.ok) return;
  const detail = await response.json().catch(() => ({})) as { message?: string };
  throw new Error(detail.message || '注册链接发送失败，请稍后重试。');
}

export async function signOut(): Promise<void> {
  intentionalSignOuts++;
  authNotice = undefined;
  try {
    // Ordinary logout affects this browser session, never the user's phones
    // or other computers. The SDK owns removal of its local auth storage.
    await supabase?.auth.signOut({ scope: 'local' });
  } finally {
    clearDeviceSession();
    intentionalSignOuts--;
  }
}

export async function setInitialPassword(password: string): Promise<void> {
  if (!supabase) throw new Error('站点尚未配置托管账户服务。');
  const updated = await supabase.auth.updateUser({ password });
  if (updated.error) throw authError(updated.error, '密码设置失败，请稍后重试。');
  const completed = await supabase.rpc('complete_first_password_setup');
  if (completed.error) throw readableError(completed.error, '账户注册未能完成，请重试。');
  await supabase.auth.refreshSession();
}

export async function changePassword(session: Session, currentPassword: string, nextPassword: string): Promise<void> {
  await deviceFetch(session, '/api/account/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, nextPassword }),
  });
}

function authError(error: unknown, fallback: string): Error {
  if (isInvalidSessionError(error)) return new Error(SESSION_EXPIRED_MESSAGE);
  const message = readableError(error, fallback).message;
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid login credentials')) return new Error('邮箱或密码错误。');
  if (normalized.includes('email not confirmed')) return new Error('请先打开邀请邮件并确认邮箱。');
  if (normalized.includes('rate limit') || normalized.includes('too many requests')) return new Error('尝试次数过多，请稍后再试。');
  if (normalized.includes('same password') || normalized.includes('different from the old password')) return new Error('新密码不能与当前密码相同。');
  if (normalized.includes('password should be at least') || normalized.includes('weak password')) return new Error('密码强度不足，请至少使用 8 位字符。');
  return new Error(message || fallback);
}

function readAuthCallbackIssue(): string | undefined {
  if (typeof location === 'undefined') return undefined;
  const sources = [new URLSearchParams(location.search), new URLSearchParams(location.hash.replace(/^#/, ''))];
  const value = (key: string): string | undefined => sources.map((params) => params.get(key)?.trim()).find(Boolean);
  const code = value('error_code')?.toLowerCase();
  const description = value('error_description');
  if (code === 'otp_expired') return '注册链接已失效或已经使用，请重新发送注册链接。';
  if (code === 'access_denied') return '邮箱验证未完成，请重新发送注册链接。';
  if (code || description) return description || '注册链接无法验证，请重新发送。';
  return undefined;
}

function readableError(error: unknown, fallback: string): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string')
    return new Error(error.message);
  if (typeof error === 'string' && error.trim()) return new Error(error);
  return new Error(fallback);
}

function autoLoginEnabled(): boolean {
  const value = safeGet(localStorage, AUTO_LOGIN_KEY);
  // Keep existing signed-in users logged in after this hot update. Once the
  // user changes the checkbox the explicit preference takes precedence.
  return value === null ? true : value === 'true';
}

function safeGet(storage: Storage, key: string): string | null {
  try { return storage.getItem(key); } catch { return null; }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try { storage.setItem(key, value); } catch { /* Private mode may deny persistent storage. */ }
}

function safeRemove(storage: Storage, key: string): void {
  try { storage.removeItem(key); } catch { /* Nothing else to clear. */ }
}

export async function listProfiles(): Promise<AccountProfile[]> {
  if (demoMode) return [await loadProfile(undefined)];
  const session = await currentSession();
  if (!session) throw new Error('管理员登录状态已失效。');
  const data = await deviceFetch<Record<string, unknown>[]>(session, '/api/admin/users');
  return data.map(profileFromRow);
}

export async function updateProfile(profile: AccountProfile): Promise<void> {
  if (demoMode) return;
  const session = await currentSession();
  if (!session) throw new Error('管理员登录状态已失效。');
  await deviceFetch(session, '/api/admin/users', { method: 'PATCH', body: JSON.stringify({
    id: profile.id,
    displayName: profile.displayName,
    role: profile.role,
    status: profile.status,
    expiresAt: profile.expiresAt ?? null,
    features: profile.features,
    device_limit: profile.deviceLimit,
    weekly_replacement_limit: profile.weeklyReplacementLimit,
    study_limit: profile.studyLimit,
    result_limit: profile.resultLimit,
  }) });
}

export async function inviteAccount(email: string, expiresAt?: string): Promise<void> {
  const session = await currentSession();
  if (!session) throw new Error('管理员登录状态已失效。');
  await deviceFetch(session, '/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, expiresAt }),
  });
}

export async function revokeAccountDevice(userId: string, deviceId: string): Promise<void> {
  const session = await currentSession();
  if (!session) throw new Error('管理员登录状态已失效。');
  await deviceFetch(session, '/api/admin/users', { method: 'DELETE', body: JSON.stringify({ userId, deviceId }) });
}

export async function deleteAccount(userId: string): Promise<void> {
  const session = await currentSession();
  if (!session) throw new Error('管理员登录状态已失效。');
  await deviceFetch(session, '/api/admin/users', { method: 'DELETE', body: JSON.stringify({ userId, deleteAccount: true }) });
}

function profileFromRow(row: Record<string, unknown>): AccountProfile {
  const expiresAt = typeof row.expires_at === 'string' ? row.expires_at : undefined;
  return {
    id: String(row.id ?? ''),
    email: String(row.email ?? ''),
    displayName: String(row.display_name ?? row.email ?? '用户'),
    role: row.role === 'admin' ? 'admin' : 'user',
    status: row.status === 'disabled' ? 'disabled' : 'active',
    ...(expiresAt ? { expiresAt } : {}),
    features: Array.isArray(row.features) ? row.features.map(String) : ['desktop', 'solver'],
    mustSetPassword: row.must_set_password === true,
    deviceLimit: boundedNumber(row.device_limit, 2),
    weeklyReplacementLimit: boundedNumber(row.weekly_replacement_limit, 2),
    studyLimit: boundedNumber(row.study_limit, 5),
    resultLimit: boundedNumber(row.result_limit, 5),
    devices: Array.isArray(row.devices) ? row.devices.map((value) => deviceFromRow(value)).filter((value): value is AuthorizedDevice => Boolean(value)) : [],
    usage: usageFromRow(row.usage),
    isSuperAdmin: row.is_super_admin === true || String(row.email ?? '').trim().toLowerCase() === 'bryan.liang@outlook.com',
    canManage: row.can_manage !== false,
  };
}

function boundedNumber(value: unknown, fallback: number): number { const parsed = Number(value); return Number.isFinite(parsed) ? parsed : fallback; }
function deviceFromRow(value: unknown): AuthorizedDevice | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const row = value as Record<string, unknown>;
  return { id: String(row.id ?? ''), label: String(row.label ?? '设备'), createdAt: String(row.createdAt ?? row.created_at ?? ''), lastSeenAt: String(row.lastSeenAt ?? row.last_seen_at ?? '') };
}
function usageFromRow(value: unknown): { studies: number; results: number } {
  if (typeof value !== 'object' || value === null) return { studies: 0, results: 0 };
  const row = value as Record<string, unknown>;
  return { studies: boundedNumber(row.studies, 0), results: boundedNumber(row.results, 0) };
}

function cacheKey(userId: string): string { return `minglun.workspace.${userId}`; }
function cacheWorkspace(userId: string, value: { workbench: Workbench; userPlan: UserPlan;updatedAt?:string|null }): void {
  localStorage.setItem(cacheKey(userId), JSON.stringify(value));
}
function readCachedWorkspace(userId: string): { workbench: Workbench; userPlan: UserPlan;updatedAt?:string|null } | undefined {
  const raw = localStorage.getItem(cacheKey(userId));
  if (!raw) return undefined;
  try { return JSON.parse(raw) as { workbench: Workbench; userPlan: UserPlan }; } catch { return undefined; }
}
function clearCachedWorkspace(userId: string): void {
  try { localStorage.removeItem(cacheKey(userId)); } catch { /* Cache is optional. */ }
}
